"use client"

import { AnimatedButtonProps } from "@/types/types"
import { isExternalLink } from "@/utils/ensure-protocol-helper"
import { useRouter } from "next/navigation"
import { useState } from "react"
import { twMerge } from "tailwind-merge"

export const AnimatedButton = ({
  button,
  index,
  totalButtons,
  buttonStyle,
  // Backward compatibility - giữ lại các props cũ
  buttonSize: buttonSizeProp,
  buttonFontSize: buttonFontSizeProp,
  buttonFontWeight: buttonFontWeightProp,
  buttonLineHeight: buttonLineHeightProp,
  hoverBackgroundColor: hoverBackgroundColorProp,
  hoverColor: hoverColorProp,
  hoverBorder: hoverBorderProp,
  hoverTransform: hoverTransformProp,
  hoverOpacity: hoverOpacityProp,
  hoverBoxShadow: hoverBoxShadowProp,
}: AnimatedButtonProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const router = useRouter()
  const isPrimary = button.buttonType === "primary"
  const bgColor = button.buttonBg || "#18AF4E"

  const buttonId = `animated-button-${index}-${Math.random().toString(36).substr(2, 9)}`

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!button.buttonLink) return

    if (isExternalLink(button.buttonLink)) {
      window.open(button.buttonLink, "_blank", "noopener,noreferrer")
    } else {
      router.push(button.buttonLink)
    }
  }

  // Ưu tiên sử dụng buttonStyle object, fallback về các props riêng lẻ
  const buttonSize = buttonStyle?.buttonSize || buttonSizeProp
  const buttonFontSize = buttonStyle?.buttonFontSize || buttonFontSizeProp
  const buttonFontWeight = buttonStyle?.buttonFontWeight || buttonFontWeightProp
  const buttonLineHeight = buttonStyle?.buttonLineHeight || buttonLineHeightProp
  const hoverBackgroundColor = buttonStyle?.hoverBackgroundColor || hoverBackgroundColorProp
  const hoverColor = buttonStyle?.hoverColor || hoverColorProp
  const hoverBorder = buttonStyle?.hoverBorder || hoverBorderProp
  const hoverTransform = buttonStyle?.hoverTransform || hoverTransformProp
  const hoverOpacity = buttonStyle?.hoverOpacity || hoverOpacityProp
  const hoverBoxShadow = buttonStyle?.hoverBoxShadow || hoverBoxShadowProp

  const baseStyle: React.CSSProperties = {
    position: "relative",
    color: "#fff",
    borderRadius: "4px",
    fontWeight: buttonFontWeight ? parseInt(buttonFontWeight) : 700,
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: isPrimary ? "0" : "1px solid #fff",
    background: isPrimary ? bgColor : "transparent",
    transition: "filter 0.3s ease, background-color 0.3s ease",
    cursor: "pointer",
    zIndex: 2,
    overflow: "visible",
    ...(buttonSize?.width && { width: buttonSize.width }),
    ...(buttonSize?.height && { height: buttonSize.height }),
    ...(buttonFontSize && { fontSize: buttonFontSize }),
    ...(buttonLineHeight && { lineHeight: buttonLineHeight }),
  }

  const hoverStyle: React.CSSProperties = {
    ...baseStyle,
    // Use custom hover styles if provided, otherwise use default
    background: hoverBackgroundColor ? hoverBackgroundColor : isPrimary ? bgColor : "rgba(255, 255, 255, 0.1)",
    color: hoverColor || baseStyle.color,
    border: hoverBorder || baseStyle.border,
    transform: hoverTransform || undefined,
    opacity: hoverOpacity ? parseFloat(hoverOpacity) : undefined,
    boxShadow: hoverBoxShadow || undefined,
    filter: hoverBackgroundColor ? "none" : isPrimary ? "brightness(1.2)" : "none",
  }

  return (
    <>
      <span
        id={buttonId}
        onClick={handleClick}
        data-aos="aos-fade-in-up"
        data-aos-duration="1000"
        data-aos-delay={`${400 + index * 100}`}
        className={twMerge(
          !buttonSize?.width && "w-[97px] sm:w-[104px]",
          !buttonSize?.height && "h-[46px]",
          !buttonFontSize && "text-sm sm:text-base",
          !buttonLineHeight && "leading-[46px] sm:leading-[46px]",
        )}
        style={isHovered ? hoverStyle : baseStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {button.buttonName}
        <span
          className={twMerge(
            isHovered ? "aos-animate" : "",
            "absolute top-1/2 left-1/2 w-[116.4px] h-[55.2px] sm:w-[124.6px] sm:h-[55.2px]",
          )}
          data-aos="aos-fade-in-up"
          data-aos-duration="800"
          style={{
            border: `2px solid ${bgColor}`,
            borderRadius: "4px",
            pointerEvents: "none",
            opacity: isHovered ? 1 : 0,
            zIndex: -1,
            boxSizing: "border-box",
            transformOrigin: "center center",
            transform: isHovered ? "translate(-50%, -50%) scale(0.8333)" : "translate(-50%, -50%) scale(1)",
            transition: "transform 0.6s cubic-bezier(0.6, 0, 0.1, 1), opacity 0.6s cubic-bezier(0.6, 0, 0.1, 1)",
          }}
        />
      </span>
    </>
  )
}
